"use client";

import * as React from "react";

import { useLocalizedInput } from "@/hooks/useLocalizedInput";
import {
  getInputNormalizer,
  NUMBER_INPUT_PATTERN,
} from "@/lib/forms/localized-input";
import { cn } from "@/lib/utils";

export interface InputProps extends React.ComponentProps<"input"> {
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  /** Let a form preserve the native IME buffer while syncing explicit value changes. */
  browserOwned?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      startAdornment,
      endAdornment,
      dir,
      inputMode,
      value,
      defaultValue,
      onChange,
      onBlur,
      browserOwned,
      onInput,
      onCompositionStart,
      onCompositionEnd,
      ...props
    },
    ref,
  ) => {
    const hasAdornments =
      startAdornment !== undefined || endAdornment !== undefined;
    const isNumberInput = type === "number";
    const resolvedDir = isNumberInput ? "ltr" : dir;
    const normalize = getInputNormalizer(type, inputMode);
    const { valueProps, inputEvents } = useLocalizedInput<HTMLInputElement>({
      normalize,
      browserOwned,
      ref,
      value,
      defaultValue,
      numberConstraints: isNumberInput ? props : undefined,
      nativeInputFallback:
        !type ||
        [
          "text",
          "number",
          "tel",
          "search",
          "email",
          "url",
          "password",
        ].includes(type),
      onChange,
      onBlur,
      onInput,
      onCompositionStart,
      onCompositionEnd,
    });
    // Native number inputs discard Persian digits before handlers can read
    // them. A text control with a decimal keyboard lets us normalize first.
    const localizedProps = {
      ...props,
      ...inputEvents,
      type: isNumberInput ? "text" : type,
      inputMode: inputMode ?? (isNumberInput ? "decimal" : undefined),
      ...valueProps,
      pattern:
        props.pattern ?? (isNumberInput ? NUMBER_INPUT_PATTERN : undefined),
    };

    const defaultClassNames = cn(
      // Layout & Box Model
      "flex h-9 w-full",
      // Border & Radius
      "rounded-xs border border-stroke-light",
      // Background
      "bg-transparent",
      // Padding
      "p-3",
      // Typography
      "text-xs md:text-sm file:text-sm file:font-medium file:text-foreground",
      // Shadow
      "shadow-sm",
      // Transition
      "transition-colors",
      // File Input
      "file:border-0 file:bg-transparent",
      // Placeholder
      "placeholder:text-text-secondary placeholder:text-xs",
      // Focus
      "focus-visible:outline-none focus-visible:border-stroke-primary",
      // Disable
      "disabled:cursor-not-allowed disabled:opacity-50",
      className,
      isNumberInput &&
        "text-left [appearance:textfield] [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none",
    );

    if (!hasAdornments) {
      // Return the original input without adornments
      return (
        <input
          dir={resolvedDir}
          className={cn(defaultClassNames)}
          {...localizedProps}
        />
      );
    }

    return (
      <div className="relative w-full" dir={resolvedDir}>
        {startAdornment !== undefined && (
          <div className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            {startAdornment}
          </div>
        )}
        <input
          dir={resolvedDir}
          className={cn(
            defaultClassNames,

            // Adjust padding based on adornments
            // Check for null specifically - null means no adornment, undefined means adornment container exists (for animations)
            startAdornment !== null && startAdornment !== undefined
              ? "ps-10"
              : "ps-3",
            endAdornment !== null && endAdornment !== undefined
              ? "pe-10"
              : "pe-3",
            className,
          )}
          {...localizedProps}
        />
        {endAdornment !== undefined && (
          <div className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            {endAdornment}
          </div>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
